import { useEffect, useState } from 'react';
import { JSONContent } from '@tiptap/core';

import type { GetClassStudentAnswersQuery } from '../../present/activity-answers.graphql.generated';
import { useRichtext } from 'approot/shared/richtext/richtext.hooks';
import { BORDER_STYLE_NONE } from 'global/constants';
import Richtext from 'approot/shared/richtext/richtext';
import { Field } from './response.constants';
import { useResponseRichtextSettings } from './response.hooks';
import * as Styled from './response-view.style';
import { getInitialContent } from 'approot/shared/richtext/get-initial-richtext-data';
import { getRichtextAnswer } from '../shared/logic/get-richtext-answer';
import type { Pluck } from 'lib/gql/pluck';

const ResponseField = ({
  field,
  answer,
  baseHeight,
  ariaLabelledBy,
  ariaDescribedby,
  onChange,
  onFocus,
}: {
  field: Field;
  answer:
    | null
    | Pluck<GetClassStudentAnswersQuery, 'getClassStudentsAnswers'>
    | undefined;
  baseHeight: number;
  ariaLabelledBy?: string;
  ariaDescribedby?: string;
  onChange?: (content: JSONContent) => void;
  onFocus?: () => void;
}) => {
  const settings = useResponseRichtextSettings(field);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const { editor, toolbarRef } = useRichtext({
    initialValue: getInitialContent({
      initialValue: getRichtextAnswer(answer),
      settings,
    }),
    onChange: json => onChange?.(json),
    onFocus: () => {
      setIsEditing(true);
      onFocus?.();
    },
    onBlur: () => setIsEditing(false),
    settings,
  });

  useEffect(() => {
    // answers come in separately, only initialise once
    if (answer && !isEditing) {
      const editorContent = getInitialContent({
        initialValue: getRichtextAnswer(answer),
        settings,
      });
      editor?.commands.setContent(
        editorContent
          ? editorContent
          : {
              type: 'doc',
              content: [],
            }
      );
    }
  }, [answer, isEditing, editor, settings]);

  return (
    <Styled.ResponseBox variant={BORDER_STYLE_NONE}>
      <Styled.ResponseField
        $numberOflines={field.numberOfLines || 1}
        $baseHeight={baseHeight}
        $baseColour={field.baseColour}
      >
        {editor && (
          <Richtext
            editor={editor}
            toolbarRef={toolbarRef}
            isSmall
            ariaLabelledBy={ariaLabelledBy}
            ariaDescribedBy={ariaDescribedby}
          />
        )}
      </Styled.ResponseField>
    </Styled.ResponseBox>
  );
};

export default ResponseField;
